import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, FileSearch, FileText, Rocket, LifeBuoy, ShieldCheck, Calculator, Users, Globe } from "lucide-react";
import ComplexityTiers from "@/components/ComplexityTiers";
import { GRUENDUNG_TIERS, STEUER_TIERS } from "@/data/complexityTiers";

const processSteps = [
  {
    Icon: Phone,
    title: "01 · Erstgespräch",
    desc: "Unverbindlich, 30–45 Min, telefonisch oder per Video. Sie schildern Ihre Situation, wir geben eine erste fachliche Einordnung – ohne Verkaufsdruck.",
    out:  "Klare Empfehlung: einfach, standard oder speziell. Grobe Aufwandsschätzung.",
  },
  {
    Icon: FileSearch,
    title: "02 · Analyse & Mandatsaufnahme",
    desc: "Wir prüfen Unterlagen, klären KYC-/AML-Fragen, identifizieren steuerliche Hebel im Verhältnis DE ↔ CH und definieren das Zielbild gemeinsam mit Ihnen.",
    out:  "Mandatsvertrag, Vollmacht, KYC-Akte vollständig.",
  },
  {
    Icon: FileText,
    title: "03 · Konzept & Angebot",
    desc: "Sie erhalten ein detailliertes, schriftliches Angebot – inklusive Fahrplan, Meilensteinen und einer transparenten Kostenstruktur (Pauschale, Stundensatz oder gemischt).",
    out:  "Kostenvoranschlag (PDF) + Roadmap im Portal.",
  },
  {
    Icon: Rocket,
    title: "04 · Umsetzung",
    desc: "Wir kümmern uns um Gründung, Bankeröffnung, Statuten, Handelsregister, Steueranmeldung, Domiziladresse und – falls gewünscht – das Verwaltungsratsmandat.",
    out:  "Gesellschaft eingetragen, operativ einsatzbereit.",
  },
  {
    Icon: LifeBuoy,
    title: "05 · Laufende Betreuung",
    desc: "Buchhaltung, MWST, Lohn, Jahresabschluss, Steuererklärung. Bei komplexen Strukturen begleiten wir Sie laufend in Steuer-, Nachfolge- und Vermögensthemen.",
    out:  "Fester Ansprechpartner – kein Ticket-System.",
  },
];

const focusTopics = [
  { Icon: Calculator,  title: "Steuern sparen ggü. Deutschland", desc: "Tiefere Steuerbelastung in der Schweiz, gezielter Einsatz des Doppelbesteuerungsabkommens DE–CH, optimale Mischung aus Lohn und Dividende." },
  { Icon: ShieldCheck, title: "Geldwerte absichern",            desc: "Schutz von Vermögen vor behördlichem Zugriff durch saubere rechtliche Struktur – Trennung Betriebs- / Privatvermögen, klare Eigentumsverhältnisse." },
  { Icon: Users,       title: "Vererbung & Nachfolge",          desc: "Steueroptimierte Übertragung von Firmenwerten und Vermögen an die nächste Generation – auch im grenzüberschreitenden DE/CH-Erbfall." },
  { Icon: Globe,       title: "Schweiz–Deutschland verstehen",  desc: "Wegzugsbesteuerung (§ 6 AStG), Aufenthaltsstatus, Holding-Struktur, Quellensteuer – wir orchestrieren das Zusammenspiel beider Welten." },
];

export default function WieFunktioniertPage() {
  return (
    <div className="pt-32 pb-24" data-testid="howto-page">
      <div className="mbw-container">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="mbw-overline mb-4">So funktioniert's</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
            Was kostet was – <span className="text-[#991B1B]">und warum.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Wir arbeiten transparent: gleicher Prozess für alle Mandate, aber abgestufter
            Aufwand. Lesen Sie hier, wann ein Fall <strong>einfach</strong>, <strong>standard</strong> oder <strong>speziell</strong> ist –
            und welche Themen wir typischerweise für unsere DACH-Kunden lösen.
          </p>
        </div>

        {/* Schwerpunkte */}
        <section className="mb-20" data-testid="howto-focus">
          <div className="mb-8 max-w-2xl">
            <div className="mbw-overline mb-3">Unsere Schwerpunkte für DACH-Kunden</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight">
              Vier Themen, bei denen wir den größten Hebel sehen.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {focusTopics.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200" data-testid={`howto-focus-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-[#1A2433] flex items-center justify-center mb-4">
                  <t.Icon className="w-6 h-6 text-[#DC2626]" />
                </div>
                <div className="font-heading font-bold text-[#1A2433] mb-2 leading-snug">{t.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prozess */}
        <section className="mb-20" data-testid="howto-process">
          <div className="mb-8 max-w-2xl">
            <div className="mbw-overline mb-3">Der Ablauf</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight">
              Fünf Schritte vom Erstkontakt bis zur laufenden Betreuung.
            </h2>
          </div>
          <div className="space-y-4">
            {processSteps.map((p, i) => (
              <div
                key={i}
                className="grid md:grid-cols-12 gap-5 items-start bg-white border border-slate-200 rounded-xl p-6 md:p-8"
                data-testid={`howto-step-${i}`}
              >
                <div className="md:col-span-3 flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#991B1B] flex items-center justify-center flex-shrink-0">
                    <p.Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-[#1A2433] leading-tight">{p.title}</h3>
                </div>
                <div className="md:col-span-9">
                  <p className="text-slate-600 leading-relaxed mb-3">{p.desc}</p>
                  <div className="text-sm text-slate-500 border-l-2 border-[#991B1B]/40 pl-3">
                    <span className="font-semibold text-[#1A2433]">Ergebnis:</span> {p.out}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Komplexitätsmodell – Firmengründung */}
        <section className="mb-20" data-testid="howto-gruendung">
          <div className="mb-8 max-w-2xl">
            <div className="mbw-overline mb-3">Komplexität · Firmengründung</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight">
              Wo liegt Ihr Gründungsfall?
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              Die meisten Mandate ordnen wir nach diesen drei Stufen ein. Sie geben Ihnen eine
              ehrliche Vorstellung vom zu erwartenden Aufwand – ohne unrealistische Pauschalversprechen.
            </p>
          </div>
          <ComplexityTiers tiers={GRUENDUNG_TIERS} testIdPrefix="howto-gruendung" />
        </section>

        {/* Komplexitätsmodell – Steuerberatung */}
        <section className="mb-20" data-testid="howto-steuer">
          <div className="mb-8 max-w-2xl">
            <div className="mbw-overline mb-3">Komplexität · Steuerberatung</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] tracking-tight">
              Von der Erklärung bis zur Vermögensstruktur.
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              Bei Steuern ist der Unterschied zwischen „einfach" und „speziell" oft viel größer
              als bei der Gründung. Hier sehen Sie, was bei uns konkret in welche Stufe fällt.
            </p>
          </div>
          <ComplexityTiers tiers={STEUER_TIERS} testIdPrefix="howto-steuer" />
        </section>

        {/* CTA */}
        <div className="bg-[#1A2433] text-white rounded-2xl p-10 md:p-14 text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">Bereit für eine ehrliche Einschätzung?</h3>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Im Erstgespräch sagen wir Ihnen offen, in welche Komplexitätsstufe Ihr Fall fällt – und ob wir die Richtigen für Sie sind.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/kontakt" className="btn-primary" data-testid="howto-cta-contact">
              Erstgespräch vereinbaren <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/rechner" className="btn-inverse" data-testid="howto-cta-calc">
              Zum Kostenrechner
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
